import { useMemo } from 'react'

import { useGetExplorerDataWithGamesQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { Game } from '../types/Game'

/**
 * Returns the games shown in the "Other {subgenre} games" modal.
 *
 * Uses the same `/games/explorer/market` endpoint as Market Explorer so the
 * modal shows the top-performance games that match the results found on the
 * Market Explorer page (GR-2943). The previous search endpoint returned a
 * different set of games that did not match Market Explorer.
 *
 * The currently viewed game is always merged into the list (deduped by id) so
 * users can see where that specific title sits among its competitors. The
 * explorer endpoint gates its results by GPS/rank, so a non-analyzed or
 * low-ranked current game would otherwise be missing from the response.
 */
const useGetOtherSubgenreGames = (game: Game, marketIso: string, skip: boolean = false) => {
  const userLanguage = useCurrentUserLanguage()

  const { data, isFetching } = useGetExplorerDataWithGamesQuery(
    {
      marketIso,
      conventionalSubgenreIds: { [game.conventionalSubgenreId]: true },
      dataFilters: {},
      ownGames: false,
      userLanguage,
    },
    { skip: skip || !game.conventionalSubgenreId }
  )

  const games: Game[] = useMemo(() => {
    const explorerGames = data?.games ?? []
    const currentGameIncluded = explorerGames.some((explorerGame) => explorerGame.id === game.id)
    return currentGameIncluded ? explorerGames : [game, ...explorerGames]
  }, [data?.games, game])

  return {
    data: games,
    isFetching,
  }
}

export default useGetOtherSubgenreGames
